import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { allFields, isFieldVisible, questionnaireSections, type QuestionnaireField } from '../shared/questionnaire';
import {
	appointmentRows,
	explicitAppointmentRows,
	groupAppointments,
	copyAppointmentPerson,
	reusablePersonTarget,
	savedAppointmentPeople,
	type AppointmentRow,
} from '../shared/appointmentPeople';
import type { AnswerMap, JsonValue } from '../shared/types';
import { revealSection } from './feedback';
import { NameInput } from './NameInput';
import { AddressInput } from './AddressInput';
import { displayCents, incomeSummary, personOptions } from '../shared/income';

interface Props {
	answers: AnswerMap;
	onChange: (fieldId: string, value: JsonValue) => void | Promise<void>;
	onFinish?: () => Promise<void>;
	showProgress?: boolean;
	finishLabel?: string;
	disabled?: boolean;
	fieldMeta?: Record<string, { actorType: 'client' | 'staff'; updatedAt: string }>;
}

export function QuestionnaireEditor({
	answers,
	onChange,
	onFinish,
	showProgress = true,
	finishLabel = 'Finish for now',
	disabled = false,
	fieldMeta,
}: Props) {
	const [sectionIndex, setSectionIndex] = useState(0);
	const [continuing, setContinuing] = useState(false);
	const pendingSave = useRef<Promise<void>>(Promise.resolve());
	const sectionHeadingRef = useRef<HTMLHeadingElement>(null);
	const sectionHasChanged = useRef(false);
	const section = questionnaireSections[sectionIndex];
	const percent = Math.round(((sectionIndex + 1) / questionnaireSections.length) * 100);
	const visibleFields = useMemo(() => section.fields.filter((field) => isFieldVisible(field, answers)), [section, answers]);

	useEffect(() => {
		if (!sectionHasChanged.current) return;
		sectionHasChanged.current = false;
		revealSection(sectionHeadingRef.current);
	}, [sectionIndex]);

	function change(fieldId: string, value: JsonValue) {
		const result = onChange(fieldId, value);
		if (result) pendingSave.current = result;
	}

	async function continueToNextSection() {
		setContinuing(true);
		try {
			await pendingSave.current;
			sectionHasChanged.current = true;
			setSectionIndex((value) => Math.min(questionnaireSections.length - 1, value + 1));
		} catch {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		} finally {
			setContinuing(false);
		}
	}

	return (
		<div className="questionnaire-editor">
			{showProgress && (
				<div className="progress-wrap" aria-label={`Section ${sectionIndex + 1} of ${questionnaireSections.length}`}>
					<div className="progress-label">
						<span>
							Section {sectionIndex + 1} of {questionnaireSections.length}
						</span>
						<span>{percent}%</span>
					</div>
					<progress value={sectionIndex + 1} max={questionnaireSections.length}>
						{percent}%
					</progress>
				</div>
			)}
			<section aria-labelledby={`section-${section.id}`}>
				<h2 ref={sectionHeadingRef} id={`section-${section.id}`} tabIndex={-1}>
					{section.title}
					{section.optional && (
						<>
							{' '}
							- <em>optional</em>
						</>
					)}
				</h2>
				{section.intro && <p className="section-intro">{section.intro}</p>}
				{visibleFields.map((field) => (
					<Field
						key={field.id}
						field={field}
						answers={answers}
						value={answers[field.id]}
						onChange={(value) => change(field.id, value)}
						disabled={disabled || continuing}
						meta={fieldMeta?.[field.id]}
					/>
				))}
			</section>
			<div className="form-navigation">
				<button
					type="button"
					className="secondary"
					disabled={sectionIndex === 0 || continuing}
					onClick={() => setSectionIndex((value) => Math.max(0, value - 1))}
				>
					Back
				</button>
				{sectionIndex < questionnaireSections.length - 1 ? (
					<button type="button" disabled={continuing} aria-busy={continuing} onClick={() => void continueToNextSection()}>
						{continuing ? 'Saving...' : 'Save and continue'}
					</button>
				) : onFinish ? (
					<button type="button" onClick={() => void onFinish()}>
						{finishLabel}
					</button>
				) : null}
			</div>
		</div>
	);
}

export function Field({
	field,
	answers,
	value,
	onChange,
	disabled,
	meta,
}: {
	field: QuestionnaireField;
	answers: AnswerMap;
	value: JsonValue | undefined;
	onChange: (value: JsonValue) => void;
	disabled: boolean;
	meta?: { actorType: 'client' | 'staff'; updatedAt: string };
}) {
	const inputId = `field-${field.id.replaceAll('.', '-')}`;
	if (field.type === 'repeatable') {
		const items = field.primaryLabel ? explicitAppointmentRows(value) : appointmentRows(value);
		const maximum = field.maxItems ?? 100;
		const groups = groupAppointments(items);
		const appointmentHelp = field.id.startsWith('planning.health_proxy.')
			? 'NJ Health Care Proxy alternates are used in the order listed.'
			: field.id.startsWith('planning.poa.')
				? 'NJ and Florida POA successors may act independently, as stated in the selected template.'
				: undefined;
		const people = reusablePersonTarget(field) ? savedAppointmentPeople(answers, allFields) : [];
		const updateRow = (index: number, row: AppointmentRow) => onChange(items.map((item, i) => (i === index ? row : item)));
		const renderRow = (item: AppointmentRow, index: number) => (
			<div className="repeat-item" key={typeof item._id === 'string' ? item._id : index}>
				<div className="repeat-heading">
					<strong>
						{field.primaryLabel
							? `${item._group === 'primary' ? field.primaryLabel : (field.successorLabel ?? 'Successor')} ${groups[item._group === 'primary' ? 'primary' : 'successor'].indexOf(item) + 1}`
							: choiceLabel(field, index)}
					</strong>
					<button type="button" className="text-button" disabled={disabled} onClick={() => onChange(items.filter((_, i) => i !== index))}>
						Remove
					</button>
				</div>
				{reusablePersonTarget(field) && (
					<div className="child-field">
						<label htmlFor={`${inputId}-person-${index}`}>Use a saved person</label>
						<select
							id={`${inputId}-person-${index}`}
							aria-describedby={`${inputId}-person-${index}-help`}
							value=""
							disabled={disabled}
							onChange={(event) => {
								const person = people.find((candidate) => candidate.id === event.target.value);
								if (person) updateRow(index, copyAppointmentPerson(item, person, field));
							}}
						>
							<option value="">Enter manually or select a saved person</option>
							{people
								.filter((person) => person.fieldId !== field.id || person.rowIndex !== index)
								.map((person) => (
									<option key={person.id} value={person.id}>
										{person.label}
									</option>
								))}
						</select>
						<p className="help" id={`${inputId}-person-${index}-help`}>
							Selection copies contact details. You can edit them below; later changes to the saved person will not update this entry.
						</p>
					</div>
				)}
				{field.children
					?.filter((child) => isFieldVisible(child, item))
					.map((child) => (
						<ChildField
							key={child.id}
							field={child}
							answers={answers}
							value={item[child.id]}
							disabled={disabled}
							onChange={(childValue) => updateRow(index, { ...item, [child.id]: childValue })}
						/>
					))}
			</div>
		);
		return (
			<fieldset className="field-card repeatable">
				<legend>
					{field.label}
					{field.priority && <span className="priority-badge">Important</span>}
				</legend>
				{field.help && <p className="help">{field.help}</p>}
				{appointmentHelp && !field.help?.includes(appointmentHelp) && <p className="help">{appointmentHelp}</p>}
				{meta && <FieldAttribution meta={meta} />}
				{field.primaryLabel
					? (['primary', 'successor'] as const).map((group) => (
							<section key={group} aria-labelledby={`${inputId}-${group}`}>
								<h3 id={`${inputId}-${group}`}>{group === 'primary' ? field.primaryLabel : (field.successorLabel ?? 'Successor')}</h3>
								{items.map((item, index) => (item._group === group ? renderRow(item, index) : null))}
								{groups[group].length < (field.groupedAppointments?.[group === 'primary' ? 'maxPrimary' : 'maxSuccessor'] ?? 0) && (
									<button
										type="button"
										className="secondary add-button"
										disabled={disabled}
										onClick={() => onChange([...items, { _id: crypto.randomUUID(), _group: group }])}
									>
										{group === 'primary' ? 'Add primary' : 'Add successor'}
									</button>
								)}
							</section>
						))
					: items.map(renderRow)}
				{!field.primaryLabel && items.length < maximum && (
					<button
						type="button"
						className="secondary add-button"
						disabled={disabled}
						onClick={() => onChange([...items, { _id: crypto.randomUUID() }])}
					>
						{field.addLabel ?? `Add ${singular(field.label)}`}
					</button>
				)}
				{field.id === 'assets.social_security_pension' && <IncomeSummary answers={answers} />}
			</fieldset>
		);
	}

	return (
		<div className="field-card">
			<label htmlFor={inputId}>
				{field.label}
				{field.priority && <span className="priority-badge">Important</span>}
			</label>
			{field.help && (
				<p id={`${inputId}-help`} className="help">
					{field.help}
				</p>
			)}
			{meta && <FieldAttribution meta={meta} />}
			<SimpleInput
				id={inputId}
				field={field}
				value={value}
				onChange={onChange}
				disabled={disabled}
				describedBy={field.help ? `${inputId}-help` : undefined}
			/>
		</div>
	);
}

function ChildField({
	field,
	answers,
	value,
	onChange,
	disabled,
}: {
	field: QuestionnaireField;
	answers: AnswerMap;
	value: JsonValue | undefined;
	onChange: (value: JsonValue) => void;
	disabled: boolean;
}) {
	const reactId = useId();
	const id = `child-${field.id}-${reactId.replaceAll(':', '')}`;
	return (
		<div className="child-field">
			<label htmlFor={id}>{field.label}</label>
			{field.help && (
				<p id={`${id}-help`} className="help">
					{field.help}
				</p>
			)}
			<SimpleInput
				id={id}
				field={field}
				value={value}
				answers={answers}
				onChange={onChange}
				disabled={disabled || !!field.legacyOnly}
				describedBy={field.help ? `${id}-help` : undefined}
			/>
		</div>
	);
}

function SimpleInput({
	id,
	field,
	value,
	onChange,
	disabled,
	describedBy,
	answers = {},
}: {
	id: string;
	field: QuestionnaireField;
	value: JsonValue | undefined;
	onChange: (value: JsonValue) => void;
	disabled: boolean;
	describedBy?: string;
	answers?: AnswerMap;
}) {
	const stringValue = typeof value === 'string' ? value : '';
	if (field.type === 'address') return <AddressInput id={id} value={value} onChange={onChange} disabled={disabled} />;
	if (field.personChoice) {
		const options = personOptions(answers);
		if (stringValue && !options.some((option) => option.value === stringValue))
			options.push({ value: stringValue, label: `${stringValue} (previously saved)` });
		return (
			<select
				id={id}
				value={stringValue}
				disabled={disabled}
				onChange={(event) => onChange(event.target.value)}
				aria-describedby={describedBy}
			>
				<option value="">{options.length ? 'Select a name' : 'Enter names in About you first'}</option>
				{options.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		);
	}
	if (field.humanName) {
		return <NameInput id={id} value={stringValue} disabled={disabled} aria-describedby={describedBy} onValueChange={onChange} />;
	}
	if (field.type === 'textarea')
		return (
			<textarea
				id={id}
				value={stringValue}
				disabled={disabled}
				aria-describedby={describedBy}
				onChange={(event) => onChange(event.target.value)}
				rows={3}
			/>
		);
	if (field.type === 'single' || field.type === 'yes_no') {
		return (
			<select
				id={id}
				value={stringValue}
				disabled={disabled}
				aria-describedby={describedBy}
				onChange={(event) => onChange(event.target.value)}
			>
				<option value="">Select an answer</option>
				{field.options?.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		);
	}
	if (field.type === 'multi') {
		const selected = Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
		return (
			<div className="checkbox-group" id={id}>
				{field.options?.map((option) => (
					<label key={option.value}>
						<input
							type="checkbox"
							checked={selected.includes(option.value)}
							disabled={disabled}
							onChange={(event) =>
								onChange(event.target.checked ? [...selected, option.value] : selected.filter((item) => item !== option.value))
							}
						/>
						{option.label}
					</label>
				))}
			</div>
		);
	}
	const type = field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.type === 'date' ? 'date' : 'text';
	return (
		<input
			id={id}
			type={type}
			inputMode={field.type === 'currency' ? 'decimal' : undefined}
			value={stringValue}
			disabled={disabled}
			aria-describedby={describedBy}
			onChange={(event) => onChange(event.target.value)}
		/>
	);
}

function FieldAttribution({ meta }: { meta: { actorType: 'client' | 'staff'; updatedAt: string } }) {
	return (
		<p className="field-attribution">
			Last changed by {meta.actorType === 'client' ? 'the client' : 'staff'} on {new Date(meta.updatedAt).toLocaleString()}.
		</p>
	);
}

function choiceLabel(field: QuestionnaireField, index: number): string {
	return `${singular(field.label)} ${index + 1}`;
}

export function IncomeSummary({ answers }: { answers: AnswerMap }) {
	const summary = incomeSummary(answers);
	if (!summary.entries.length) return null;
	return (
		<div className="income-summary">
			<h3>Gross income: monthly and annual equivalents</h3>
			{summary.entries.map(({ row, owner, amounts }, index) => (
				<p key={index}>
					{typeof row.source === 'string' ? row.source : 'Income'} · {owner || 'Owner not selected'}
					<br />
					Monthly: {displayCents(amounts?.monthly) || '—'} · Annual: {displayCents(amounts?.annual) || '—'}
				</p>
			))}
			<h4>Totals per person</h4>
			{summary.totals.map((total, index) => (
				<p key={index}>
					{total.owner}: Monthly {displayCents(total.monthly)} · Annual {displayCents(total.annual)}
				</p>
			))}
			{summary.incomplete > 0 && (
				<p role="status">
					Totals exclude incomplete entries. Enter a valid amount, select Monthly or Annual, and select an owner for each entry.
				</p>
			)}
			<p className="help">
				Annual amounts divided by 12 give monthly averages. Totals are calculated before rounding to cents. Older annual entries remain
				annual; to revise one, enter a new gross amount and frequency.
			</p>
		</div>
	);
}

function singular(label: string): string {
	return label.endsWith('ies') ? `${label.slice(0, -3)}y` : label.endsWith('s') ? label.slice(0, -1) : label;
}
